import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchStore } from '@shared/stores/searchStore';
import { useDebounce } from '@shared/hooks/useDebounce';
import {
  serializeFiltersToURL,
  serializePaginationToURL,
  cleanURLParams,
  deserializeURLToFilters,
  deserializePageFromURL,
} from '@shared/lib/utils/urlFilters';

/**
 * Custom hook to synchronize search filters between URL and Zustand store
 *
 * Architecture Decision:
 * - Zustand store is the SINGLE SOURCE OF TRUTH for all search state
 * - URL is updated to reflect store state (for bookmarking/sharing)
 * - On initial load, URL params are read and populate Zustand (if present)
 * - After initialization, store changes update URL (one-way: Zustand -> URL)
 * - This prevents infinite loops between URL <-> state
 *
 * Features:
 * - Initializes Zustand from URL params on first mount
 * - Updates URL when filters change in store
 * - Uses replace instead of push to avoid history pollution
 * - Debounces updates to prevent excessive URL changes
 */
export function useURLFilterSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);

  const {
    filters,
    currentPage,
    setFiltersWithoutPageReset,
    setCurrentPage,
    setHasSearched,
  } = useSearchStore();

  // Create a debounced version of filters for URL updates
  // This prevents the URL from updating on every keystroke
  const debouncedFilters = useDebounce(filters, 500);

  // Debounce page changes separately (faster debounce for pagination)
  const debouncedPage = useDebounce(currentPage, 100);

  // Initialize Zustand from URL on first mount (URL -> Zustand)
  useEffect(() => {
    if (isInitialMount.current) {
      const searchParams = new URLSearchParams(location.search);

      // Check if URL has any params
      if (searchParams.toString()) {
        const urlFilters = deserializeURLToFilters(searchParams);
        const urlPage = deserializePageFromURL(searchParams);

        // Only update if URL has filter or pagination params
        if (Object.keys(urlFilters).length > 0) {
          // Use setFiltersWithoutPageReset to avoid resetting page when loading from URL
          setFiltersWithoutPageReset(urlFilters);
          setHasSearched(true);
        }

        // Set page if present in URL (otherwise use persisted value from Zustand)
        if (urlPage && urlPage > 1) {
          setCurrentPage(urlPage);
        }
      }

      isInitialMount.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Update URL when debounced filters or page change in store (Zustand -> URL)
  useEffect(() => {
    // Skip URL update on initial mount to avoid overwriting URL from above
    if (isInitialMount.current) return;
    // Combine filter params and pagination params
    const filterParams = serializeFiltersToURL(debouncedFilters);
    const paginationParams = serializePaginationToURL(debouncedPage);

    // Merge both param sets
    const combinedParams = new URLSearchParams(filterParams);
    for (const [key, value] of paginationParams.entries()) {
      combinedParams.set(key, value);
    }

    const cleanedParams = cleanURLParams(combinedParams);
    const newSearch = cleanedParams.toString();
    const newURL = newSearch ? `?${newSearch}` : '';

    // Only update if URL actually changed
    if (newURL !== location.search) {
      navigate(newURL, { replace: true });
    }
  }, [debouncedFilters, debouncedPage, navigate, location.search]);

  // Return utility functions
  return {
    /**
     * Get the current shareable URL
     */
    getShareableURL: () => {
      const params = serializeFiltersToURL(filters);
      const paginationParams = serializePaginationToURL(currentPage);

      const combinedParams = new URLSearchParams(params);
      for (const [key, value] of paginationParams.entries()) {
        combinedParams.set(key, value);
      }

      const cleanedParams = cleanURLParams(combinedParams);
      const queryString = cleanedParams.toString();
      return queryString
        ? `${window.location.origin}/search?${queryString}`
        : `${window.location.origin}/search`;
    },

    /**
     * Copy shareable URL to clipboard
     */
    copyShareableURL: async () => {
      const url = `${window.location.origin}/search${location.search}`;
      try {
        await navigator.clipboard.writeText(url);
        return true;
      } catch (error) {
        console.error('Failed to copy URL:', error);
        return false;
      }
    },
  };
}
